/**
 * Commits of the code repositories linked to a project (commit ↔ task links,
 * slice 1), read from the local clone through the read-only Rust command
 * `code_repo_commits` (src-tauri/src/code_repo_log.rs).
 *
 * - a 60 s cache per folder, and one read in flight per folder: opening five
 *   tasks of the same board reads the repository once;
 * - `refresh: true` bypasses the cache (the section's Refresh button);
 * - a remote-only repository (every team repository — a team project links by
 *   URL, never by folder) gets its folder on THIS device from codeRepoClones.js.
 *
 * The Tauri API is imported lazily (tests/tauriImportsAreLazy.test.js); the web
 * build has no local git and never calls this.
 */

export const CACHE_MS = 60 * 1000;
const cache = new Map(); // path → { at, log }
const inFlight = new Map(); // path → Promise<log>

async function invokeLog(repoPath, limit) {
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke('code_repo_commits', { repoPath, limit });
}

let reader = invokeLog;
/** Tests: swap the native read. Returns the previous reader. */
export function setCodeRepoReader(fn) {
  const prev = reader;
  reader = fn || invokeLog;
  return prev;
}

export function clearCodeRepoCache() {
  cache.clear();
  inFlight.clear();
}

/** A readable sentence for the Rust error codes (`code_repo_missing: …`). */
export function codeRepoErrorKind(error) {
  const text = String(error?.message || error || '');
  if (text.startsWith('code_repo_missing')) return 'missing';
  if (text.startsWith('code_repo_not_git')) return 'notGit';
  if (text.startsWith('code_repo_refused')) return 'refused';
  return 'other';
}

/**
 * @returns {Promise<{ defaultBranch, headBranch, remoteUrl, commits: Array, truncated }>}
 */
export async function loadCodeRepoLog(repoPath, { refresh = false, limit = 300, now = Date.now } = {}) {
  const key = String(repoPath || '').trim();
  if (!key) throw new Error('code_repo_refused: no folder');
  const hit = cache.get(key);
  if (!refresh && hit && now() - hit.at < CACHE_MS) return hit.log;
  if (inFlight.has(key)) return inFlight.get(key);
  const read = (async () => {
    try {
      const raw = (await reader(key, limit)) || {};
      const log = {
        defaultBranch: raw.defaultBranch || null,
        headBranch: raw.headBranch || null,
        remoteUrl: raw.remoteUrl || null,
        commits: Array.isArray(raw.commits) ? raw.commits : [],
        truncated: !!raw.truncated,
      };
      cache.set(key, { at: now(), log });
      return log;
    } finally {
      inFlight.delete(key);
    }
  })();
  inFlight.set(key, read);
  return read;
}
